import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { useLingui } from '@lingui/react';

import { Box, Button, Flex, Heading } from '@rover/kibble/core';
import BottomSheet from '@rover/kibble/official-patterns/BottomSheet';
import Modal from '@rover/kibble/official-patterns/Modal';
import { CLOSE_TEXT } from '@rover/kibble/patterns/ScrollableModal/content.constants';
import MQ from '@rover/kibble/styles/MediaQuery';
import useTheme from '@rover/kibble/themes/useTheme';
import useMatchMedia from '@rover/react-lib/src/hooks/useMatchMedia';
import AutoHeight from '@rover/react-lib/src/pages/sitter-profile/SitterProfilePage/utils/AutoHeight/AutoHeight';

import { SizeSystemType } from '../../core/styledSystemPropTypes';

import { INFINITE_SCROLL_QUERY_THRESHOLD, MAX_MODAL_WIDTH } from './constants';
import LoadingIndicator from './LoadingIndicator';
import { ScrollableModalContentProps, ScrollableModalProps } from './ScrollableModal.common';

const Content = ({
  children,
  isDestructive,
  isDismissable,
  isFullHeight = false,
  isLoading,
  isSubmittable,
  maxModalWidth = MAX_MODAL_WIDTH,
  buttonSize = 'default',
  forceIsMobile,
  submitButtonText,
  submitButtonProps,
  submitButtonVariant,
  closeButtonVariant,
  maxModalHeight,
  scrollbarGutter,
  footerContent,
  onClose,
  onSubmit,
}: ScrollableModalContentProps): JSX.Element => {
  const { i18n } = useLingui();
  const { space } = useTheme();
  const mediaMatchesMobile = useMatchMedia(MQ.MOBILE.getBreakpointString());
  const isMobile = forceIsMobile || mediaMatchesMobile;
  const buttonsDivRef = useRef<HTMLDivElement | null>(null);
  const [buttonsDivHeight, setButtonsDivHeight] = React.useState(0);

  const contentMaxHeight = useMemo((): string | SizeSystemType => {
    if (maxModalHeight && !isMobile) {
      return maxModalHeight;
    }
    const maxHeight = isMobile && !isFullHeight ? '85vh' : '100vh';
    // Mobile: header height + padding bottom + buttons height
    // Web: padding top and bottom + buttons div height
    const extraHeight = isMobile ? 48 + 24 + buttonsDivHeight : 24 * 2 + buttonsDivHeight;

    return `calc(${maxHeight} - ${extraHeight}px)`;
  }, [buttonsDivHeight, isFullHeight, isMobile, maxModalHeight]);

  useLayoutEffect(() => {
    if (buttonsDivRef?.current?.clientHeight) {
      const height = buttonsDivRef.current.clientHeight + 16; // buttons height + buttons top margin
      setButtonsDivHeight(height);
    }
  }, [buttonsDivRef]);

  return (
    <Flex
      flexDirection="column"
      height="100%"
      width={['100%', maxModalWidth.toString()]}
      position="relative"
    >
      <Flex
        className="modal-auto-height"
        flexDirection="column"
        alignItems="center"
        px="4x"
        position="relative"
        maxHeight={contentMaxHeight}
        flex="1"
        minHeight="0"
        sx={{
          overflowY: isLoading ? 'hidden' : 'auto',
          overflowX: 'hidden',
          ...(scrollbarGutter
            ? {
                scrollbarGutter,
              }
            : undefined),
        }}
      >
        <LoadingIndicator isLoading={isLoading} />
        <AutoHeight
          sx={{
            visibility: isLoading ? 'hidden' : 'visible',
            paddingBottom: space['1x'],
          }}
        >
          {children}
        </AutoHeight>
      </Flex>
      {isSubmittable || isDismissable || footerContent ? (
        <Box width="100%" ref={buttonsDivRef} px="4x" py="4x" background="primary">
          {isSubmittable ? (
            <Button
              fullWidth
              onPress={onSubmit}
              mb={isDismissable ? '3x' : '0x'}
              variant={submitButtonVariant}
              size={buttonSize}
              destructive={isDestructive}
              {...submitButtonProps}
            >
              {submitButtonText}
            </Button>
          ) : null}
          {isDismissable ? (
            <Button
              sx={{ marginBottom: isMobile ? space['1x'] : space['0x'] }}
              fullWidth
              onPress={onClose}
              size={buttonSize}
              variant={closeButtonVariant}
            >
              {i18n._(CLOSE_TEXT)}
            </Button>
          ) : null}
          {footerContent || null}
        </Box>
      ) : null}
    </Flex>
  );
};

const ScrollableModal = ({
  title,
  children,
  closeOnDragDown = false,
  shouldCloseOnOverlayClick = true,
  headerId,
  infiniteScrollQueryThreshold = INFINITE_SCROLL_QUERY_THRESHOLD,
  isDestructive,
  isDismissable = true,
  isFullHeight = false,
  isLoading = false,
  isOpen,
  isScrollable = false,
  isSubmittable = false,
  maxModalWidth = MAX_MODAL_WIDTH,
  maxModalHeight,
  forceIsMobile,
  scrollIDPrefix = 'scrollable-modal-item',
  scrollIndex,
  submitButtonText,
  buttonSize,
  submitButtonProps,
  submitButtonVariant = 'primary',
  closeButtonVariant = 'secondary',
  showCloseIcon,
  showCloseIconMobile,
  contentProps,
  scrollbarGutter,
  footerContent,
  onClose = () => {},
  onNewPageRequest = () => {},
  onSubmit = () => {},
}: ScrollableModalProps): JSX.Element | null => {
  // Mobile devices with high px density render modals
  // override this at consumer and use isMobileBrowser if needed
  const mediaMatchesMobile = useMatchMedia(MQ.MOBILE.getBreakpointString());
  const isMobile = forceIsMobile || mediaMatchesMobile;
  const [shouldWait, setShouldWait] = React.useState(true);

  // isMobile is initially set to undefined, so we delay rendering the component until we know the device size
  // this is to prevent initially rendering the wrong modal type and then re-rendering the correct one
  useEffect(() => {
    const timeout = setTimeout(() => {
      setShouldWait(false);
    }, 100);
    return () => clearTimeout(timeout);
  }, []);

  const handleScroll = useCallback(
    (ev: Event): void => {
      const target = ev.target as HTMLElement;
      const position = target.scrollTop;
      const { scrollHeight, clientHeight } = target;
      const scrollPercentage = (position / (scrollHeight - clientHeight)) * 100;

      if (scrollPercentage >= infiniteScrollQueryThreshold) {
        onNewPageRequest();
      }
    },
    [infiniteScrollQueryThreshold, onNewPageRequest]
  );

  useEffect(() => {
    if (isScrollable) {
      let selector;

      const scrollTimeout = setTimeout(() => {
        selector = document.querySelector('.modal-auto-height');
        if (selector) {
          selector.addEventListener('scroll', handleScroll);
        }
      }, 100);

      return () => {
        clearTimeout(scrollTimeout);

        if (selector) {
          selector.removeEventListener('scroll', handleScroll);
        }
      };
    }

    return () => {};
  }, [isScrollable, children, handleScroll]);

  // Web-specific scrolling logic
  useEffect(() => {
    if (scrollIndex == null || scrollIndex <= 0) return;

    const scrollTimeout = setTimeout(() => {
      const itemToFocus = document.getElementById(`${scrollIDPrefix}-${scrollIndex}`);
      if (itemToFocus) {
        itemToFocus.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
        (itemToFocus.firstChild as HTMLElement)?.focus();
      }
    }, 100);

    // eslint-disable-next-line consistent-return
    return () => clearTimeout(scrollTimeout);
  }, [scrollIDPrefix, scrollIndex]);

  // Delaying rendering to allow isMobile to be determined and prevent rendering incorrect modal type
  if (isMobile === undefined && shouldWait) {
    return null;
  }

  if (isMobile) {
    return (
      <BottomSheet
        isOpen={isOpen}
        onRequestClose={onClose}
        closeOnDragDown={closeOnDragDown}
        isDismissible={shouldCloseOnOverlayClick}
        isFullHeight={isFullHeight}
        headerId={headerId}
        HeaderComponent={
          title ? (
            <Heading size="300" mb="0x" pl="4x" id={headerId}>
              {title}
            </Heading>
          ) : undefined
        }
        hideCloseIcon={!showCloseIconMobile}
        contentPaddingStyles={{ paddingX: '0x' }}
        // The scrolling is handled by the content itself.
        overflowY="hidden"
      >
        <Content
          isDestructive={isDestructive}
          isDismissable={isDismissable}
          isFullHeight={isFullHeight}
          isLoading={isLoading}
          isSubmittable={isSubmittable}
          forceIsMobile={isMobile}
          onClose={onClose}
          submitButtonText={submitButtonText}
          submitButtonProps={submitButtonProps}
          buttonSize={buttonSize}
          submitButtonVariant={submitButtonVariant}
          closeButtonVariant={closeButtonVariant}
          scrollbarGutter={scrollbarGutter}
          footerContent={footerContent}
          onSubmit={onSubmit}
        >
          {children}
        </Content>
      </BottomSheet>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      headerId={headerId}
      headerText={title}
      onRequestClose={onClose}
      shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
      showCloseIcon={showCloseIcon}
      closeIconSize="small"
      contentProps={{ py: '6x', px: '2x', ...contentProps }}
      headerProps={{ px: '4x' }}
    >
      <Content
        isDestructive={isDestructive}
        isDismissable={isDismissable}
        isFullHeight={isFullHeight}
        isLoading={isLoading}
        isSubmittable={isSubmittable}
        maxModalWidth={maxModalWidth}
        maxModalHeight={maxModalHeight}
        forceIsMobile={isMobile}
        onClose={onClose}
        submitButtonText={submitButtonText}
        submitButtonProps={submitButtonProps}
        submitButtonVariant={submitButtonVariant}
        closeButtonVariant={closeButtonVariant}
        buttonSize={buttonSize}
        scrollbarGutter={scrollbarGutter}
        footerContent={footerContent}
        onSubmit={onSubmit}
      >
        {children}
      </Content>
    </Modal>
  );
};

export default ScrollableModal;
